import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { updateGame } from 'store/auth'
import EditPencilIcon from 'assets/icons/edit-pencil.svg'

function GameHeader({ gameName, joinCode, gameInfo, lastCreatedGame, onCopyUrl, isGameCreator }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [editGameTitle, setEditGameTitle] = useState(false)
  const [editGameDescription, setEditGameDescription] = useState(false)
  const [editTitleValue, setEditTitleValue] = useState('')
  const [editDescriptionValue, setEditDescriptionValue] = useState('')
  const [savingGame, setSavingGame] = useState(false)

  const handleSaveGameTitle = useCallback(async () => {
    setSavingGame(true)
    try {
      await dispatch(
        updateGame({
          name: editTitleValue,
          description: gameInfo?.description ?? ''
        })
      )
      setEditGameTitle(false)
    } catch (err) {
      toast.error(err.response?.data?.message || t('lobby.start_error'))
    } finally {
      setSavingGame(false)
    }
  }, [dispatch, gameInfo?.description, editTitleValue, t])

  const handleSaveGameDescription = useCallback(async () => {
    setSavingGame(true)
    try {
      await dispatch(
        updateGame({
          name: gameInfo?.name ?? '',
          description: editDescriptionValue
        })
      )
      setEditGameDescription(false)
    } catch (err) {
      toast.error(err.response?.data?.message || t('lobby.start_error'))
    } finally {
      setSavingGame(false)
    }
  }, [dispatch, gameInfo?.name, editDescriptionValue, t])

  const handleCopyCode = () => {
    if (joinCode) {
      navigator.clipboard.writeText(joinCode)
      toast.success(t('lobby.join_code_copied'))
    }
  }

  return (
    <div className="lobby__header">
      <div className="lobby__header-top">
        <div className="lobby__header-left">
          {/* eslint-disable-next-line no-nested-ternary */}
          {!editGameTitle ? (
            <div className="lobby__game-name-row">
              <h2 className="lobby__game-name">{gameName}</h2>
              {joinCode && isGameCreator && (
                <button
                  type="button"
                  className="lobby__edit-icon-btn"
                  onClick={() => {
                    setEditTitleValue(gameInfo?.name ?? lastCreatedGame?.name ?? '')
                    setEditGameTitle(true)
                  }}
                  aria-label={t('lobby.edit')}
                >
                  <img src={EditPencilIcon} alt="" className="lobby__edit-icon-btn-img" />
                </button>
              )}
            </div>
          ) : isGameCreator ? (
            <div className="lobby__game-edit-row">
              <input
                type="text"
                className="lobby__form-input lobby__game-name-input"
                value={editTitleValue}
                onChange={(e) => setEditTitleValue(e.target.value)}
                placeholder={t('lobby.game_title')}
              />
              <button
                type="button"
                className="lobby__save-icon-btn"
                onClick={handleSaveGameTitle}
                disabled={savingGame}
                aria-label={t('lobby.save')}
              >
                <i className="fa-solid fa-check" />
              </button>
            </div>
          ) : (
            <div className="lobby__game-name-row">
              <h2 className="lobby__game-name">{gameName}</h2>
            </div>
          )}

          {joinCode && (
            <>
              {/* eslint-disable-next-line no-nested-ternary */}
              {!editGameDescription ? (
                <div className="lobby__game-desc-row">
                  <p className="lobby__game-description">
                    {gameInfo?.description ?? lastCreatedGame?.description ?? ''}
                  </p>
                  {isGameCreator && (
                    <button
                      type="button"
                      className="lobby__edit-icon-btn"
                      onClick={() => {
                        setEditDescriptionValue(
                          gameInfo?.description ?? lastCreatedGame?.description ?? ''
                        )
                        setEditGameDescription(true)
                      }}
                      aria-label={t('lobby.edit')}
                    >
                      <img src={EditPencilIcon} alt="" className="lobby__edit-icon-btn-img" />
                    </button>
                  )}
                </div>
              ) : isGameCreator ? (
                <div className="lobby__game-edit-row">
                  <input
                    type="text"
                    className="lobby__form-input lobby__game-desc-input"
                    value={editDescriptionValue}
                    onChange={(e) => setEditDescriptionValue(e.target.value)}
                    placeholder={t('lobby.game_description')}
                  />
                  <button
                    type="button"
                    className="lobby__save-icon-btn"
                    onClick={handleSaveGameDescription}
                    disabled={savingGame}
                    aria-label={t('lobby.save')}
                  >
                    <i className="fa-solid fa-check" />
                  </button>
                </div>
              ) : (
                <div className="lobby__game-desc-row">
                  <p className="lobby__game-description">
                    {gameInfo?.description ?? lastCreatedGame?.description ?? ''}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {joinCode && (
          <div className="lobby__game-code-box">
            <div className="lobby__game-code-info">
              <span className="lobby__game-code-label">{t('lobby.game_code')}</span>
              <div className="lobby__game-code-value">
                <span className="lobby__game-code-text">{joinCode}</span>
                <button
                  type="button"
                  className="lobby__game-code-copy"
                  onClick={handleCopyCode}
                  aria-label={t('lobby.copy_code')}
                >
                  <i className="fa-regular fa-copy" />
                </button>
              </div>
            </div>
            <div className="lobby__header-actions">
              <button type="button" className="lobby__invite-link-btn" onClick={onCopyUrl}>
                {t('lobby.invite_link')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

GameHeader.propTypes = {
  gameName: PropTypes.string.isRequired,
  joinCode: PropTypes.string,
  isGameCreator: PropTypes.bool,
  gameInfo: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string
  }),
  lastCreatedGame: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string
  }),
  onCopyUrl: PropTypes.func.isRequired
}

GameHeader.defaultProps = {
  joinCode: null,
  isGameCreator: false,
  gameInfo: null,
  lastCreatedGame: null
}

export default GameHeader
